import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Loader2, ImagePlus, Wrench } from "lucide-react";
import { useOssAuth } from "../../context/OssAuthContext";
import OssSectionPageHeader from "../../components/oss/OssSectionPageHeader";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Navigate } from "react-router-dom";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIE = [
  { value: "idraulico", label: "Idraulico" },
  { value: "elettrico", label: "Elettrico" },
  { value: "edile", label: "Edile" },
  { value: "informatico", label: "Informatico" },
  { value: "impianti", label: "Impianti" },
  { value: "altro", label: "Altro" },
];

const PRIORITA = [
  { value: "bassa", label: "Bassa" },
  { value: "normale", label: "Normale" },
  { value: "alta", label: "Alta" },
  { value: "urgente", label: "Urgente" },
];

const STATI_BADGE = {
  aperta: "bg-sky-100 text-sky-800",
  in_corso: "bg-amber-100 text-amber-800",
  chiusa: "bg-emerald-100 text-emerald-800",
  annullata: "bg-slate-100 text-slate-600",
};

const emptyForm = {
  titolo: "",
  descrizione: "",
  ubicazione: "",
  categoria: "altro",
  priorita: "normale",
  note: "",
};

export default function OssManutenzioni() {
  const { activeOss, token } = useOssAuth();
  const canAccess = Boolean(activeOss?.responsabile && activeOss?.manutenzioni_enabled);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [createdId, setCreatedId] = useState(null);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    if (!token || !canAccess) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/oss/manutenzioni/richieste`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setItems(data.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore caricamento richieste");
    } finally {
      setLoading(false);
    }
  }, [token, canAccess]);

  useEffect(() => {
    load();
  }, [load]);

  if (!canAccess) {
    return <Navigate to="/oss" replace />;
  }

  const openCreate = () => {
    setForm(emptyForm);
    setCreatedId(null);
    setOpen(true);
  };

  const save = async () => {
    if (!form.titolo.trim()) {
      toast.error("Inserisci un titolo");
      return;
    }
    setSaving(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/oss/manutenzioni/richieste`,
        form,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCreatedId(data.data?.id || null);
      toast.success("Richiesta inviata");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore invio richiesta");
    } finally {
      setSaving(false);
    }
  };

  const uploadFoto = async (file) => {
    if (!createdId || !file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await axios.post(
        `${API_URL}/api/oss/manutenzioni/richieste/${createdId}/allegati`,
        fd,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Foto allegata");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore upload foto");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-4">
      <OssSectionPageHeader
        sectionKey="manutenzioni"
        title="Manutenzioni"
        subtitle="Apri richieste di intervento per la struttura"
        toolbar={
          <div className="flex justify-end">
            <Button onClick={openCreate} className="bg-orange-600 hover:bg-orange-700">
              <Plus className="w-4 h-4 mr-1" /> Nuova richiesta
            </Button>
          </div>
        }
      />

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
        </div>
      ) : items.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center text-slate-500">
            <Wrench className="w-8 h-8 mx-auto mb-2 text-slate-300" />
            Nessuna richiesta. Tocca «Nuova richiesta» per segnalare un intervento.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {items.map((r) => (
            <Card key={r.id}>
              <CardContent className="p-4">
                <div className="flex flex-wrap items-center gap-2 mb-1">
                  <p className="font-semibold text-slate-900">{r.titolo}</p>
                  <Badge className={STATI_BADGE[r.stato] || STATI_BADGE.aperta}>
                    {(r.stato || "").replace("_", " ")}
                  </Badge>
                </div>
                <p className="text-sm text-slate-500">
                  {CATEGORIE.find((c) => c.value === r.categoria)?.label || r.categoria}
                  {r.ubicazione ? ` · ${r.ubicazione}` : ""}
                  {r.data_richiesta ? ` · ${r.data_richiesta}` : ""}
                </p>
                {r.descrizione ? (
                  <p className="text-sm text-slate-600 mt-1 line-clamp-2">{r.descrizione}</p>
                ) : null}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Nuova richiesta intervento</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <div>
              <Label>Titolo *</Label>
              <Input
                value={form.titolo}
                onChange={(e) => setForm((p) => ({ ...p, titolo: e.target.value }))}
                className="mt-1"
                disabled={!!createdId}
              />
            </div>
            <div>
              <Label>Descrizione</Label>
              <Textarea
                value={form.descrizione}
                onChange={(e) => setForm((p) => ({ ...p, descrizione: e.target.value }))}
                className="mt-1"
                rows={3}
                disabled={!!createdId}
              />
            </div>
            <div>
              <Label>Ubicazione</Label>
              <Input
                value={form.ubicazione}
                onChange={(e) => setForm((p) => ({ ...p, ubicazione: e.target.value }))}
                placeholder="Camera, zona…"
                className="mt-1"
                disabled={!!createdId}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Categoria</Label>
                <Select
                  value={form.categoria}
                  onValueChange={(v) => setForm((p) => ({ ...p, categoria: v }))}
                  disabled={!!createdId}
                >
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIE.map((c) => (
                      <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Priorità</Label>
                <Select
                  value={form.priorita}
                  onValueChange={(v) => setForm((p) => ({ ...p, priorita: v }))}
                  disabled={!!createdId}
                >
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PRIORITA.map((c) => (
                      <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {createdId ? (
              <label className="flex items-center justify-center gap-2 rounded-xl border border-dashed border-orange-300 bg-orange-50/50 p-4 cursor-pointer">
                {uploading ? (
                  <Loader2 className="w-5 h-5 animate-spin text-orange-600" />
                ) : (
                  <ImagePlus className="w-5 h-5 text-orange-600" />
                )}
                <span className="text-sm font-medium text-orange-800">Allega foto</span>
                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  className="hidden"
                  disabled={uploading}
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) uploadFoto(f);
                    e.target.value = "";
                  }}
                />
              </label>
            ) : null}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              {createdId ? "Chiudi" : "Annulla"}
            </Button>
            {!createdId ? (
              <Button
                className="bg-orange-600 hover:bg-orange-700"
                onClick={save}
                disabled={saving}
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                Invia
              </Button>
            ) : null}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
